import axios from 'axios';
export const secondsToTimeComponents = (totalSeconds) => ({
    hours: String(Math.floor(totalSeconds / 3600)),
    minutes: String(Math.floor((totalSeconds % 3600) / 60)),
    seconds: String(Math.floor(totalSeconds % 60))
});
export const formatTime = (seconds) => {
    if (seconds === 0)
        return '00:00:00';
    const { hours, minutes, seconds: secs } = secondsToTimeComponents(seconds);
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${secs.padStart(2, '0')}`;
};
export const getTimeConstraints = (isStart, startTime, endTime, durationTime) => {
    if (isStart) {
        // For start time inputs:
        // - If we're at max hours, limit minutes
        // - If we're at max hours and minutes, limit seconds
        const atMaxHours = Number(startTime.hours) === Number(endTime.hours);
        const atMaxMinutes = atMaxHours && Number(startTime.minutes) === Number(endTime.minutes);
        return {
            hours: {
                min: 0,
                max: Number(endTime.hours)
            },
            minutes: {
                min: 0,
                max: atMaxHours ? Number(endTime.minutes) : 59
            },
            seconds: {
                min: 0,
                max: atMaxMinutes ? Number(endTime.seconds) : 59
            }
        };
    }
    else {
        // For end time inputs:
        // - If we're at min hours, enforce min minutes
        // - If we're at min hours and minutes, enforce min seconds
        // - If we're at max hours, limit minutes
        // - If we're at max hours and minutes, limit seconds
        const atMinHours = Number(endTime.hours) === Number(startTime.hours);
        const atMinMinutes = atMinHours && Number(endTime.minutes) === Number(startTime.minutes);
        const atMaxHours = Number(endTime.hours) === Number(durationTime.hours);
        const atMaxMinutes = atMaxHours && Number(endTime.minutes) === Number(durationTime.minutes);
        return {
            hours: {
                min: Number(startTime.hours),
                max: Number(durationTime.hours)
            },
            minutes: {
                min: atMinHours ? Number(startTime.minutes) : 0,
                max: atMaxHours ? Number(durationTime.minutes) : 59
            },
            seconds: {
                min: atMinMinutes ? Number(startTime.seconds) : 1,
                max: atMaxMinutes ? Number(durationTime.seconds) : 59
            }
        };
    }
};
export const generateLocalVideoTimeline = async (video, onProgress) => {
    // Generate a preview every 2 seconds, with minimum 10 and maximum 50 previews to avoid lag
    const PREVIEW_INTERVAL = 2;
    const MIN_PREVIEWS = 10;
    const MAX_PREVIEWS = 50;
    const PREVIEW_COUNT = Math.min(MAX_PREVIEWS, Math.max(MIN_PREVIEWS, Math.ceil(video.duration / PREVIEW_INTERVAL)));
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        console.error('Could not get canvas context');
        return [];
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const timeInterval = video.duration / PREVIEW_COUNT;
    const localPreviews = [];
    for (let i = 0; i < PREVIEW_COUNT; i++) {
        // Calculate progress from 80 to 100
        const timelineProgress = 80 + Math.round((i / PREVIEW_COUNT) * 20);
        onProgress(timelineProgress);
        const time = i * timeInterval;
        video.currentTime = time;
        // Wait for the video to seek to the correct time
        await new Promise(resolve => {
            const handleSeeked = () => {
                video.removeEventListener('seeked', handleSeeked);
                resolve();
            };
            video.addEventListener('seeked', handleSeeked);
        });
        // Draw the current frame to canvas and convert to data URL
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/jpeg', 0.3);
        localPreviews.push({
            index: i,
            url,
            time // Add timestamp for more accurate seeking. Not used yet.
        });
    }
    video.currentTime = 0;
    return localPreviews;
};
const videoUrlToObjectUrl = async (url, onProgress) => {
    const response = await axios.get(url, {
        responseType: 'blob',
        onDownloadProgress: progressEvent => {
            const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? progressEvent.loaded)), 80);
            onProgress(percentCompleted);
        }
    });
    return URL.createObjectURL(response.data);
};
const fileToObjectUrl = async (file, onProgress) => {
    // Create a smooth fake progress animation
    const url = URL.createObjectURL(file);
    return new Promise(resolve => {
        let progress = 0;
        const interval = setInterval(() => {
            progress += 5;
            if (progress >= 90) {
                clearInterval(interval);
                onProgress(80);
                resolve(url);
            }
            else {
                onProgress(progress);
            }
        }, 30); // Update every 30ms
    });
};
export const videoToObjectUrl = async (video, onProgress) => {
    if (typeof video === 'string') {
        return videoUrlToObjectUrl(video, onProgress);
    }
    return fileToObjectUrl(video, onProgress);
};
